/**
 * This file defines the Contact component for the React application.
 *
 * This page will hold the form for users to contact the organization.
 * It uses Web3Forms to handle form submissions.
 *
 * How to get access key from Web3Forms:
 *
 * 1) Go to https://web3forms.com/ and enter your email in step 1.
 * 2) Your access key will be sent to your email.
 * 3) Enter this access key into line 68 of this file.
 *
 * Author: Jennifer Tran
 * Created on: 2024-10-26
 * Professor: Mejdi Eraslan
 * Client: Melisa Eraslan
 */

import React, { useState } from 'react';
import '../css/Contact.css';

const Contact = () => {
    // State to manage form inputs
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        purpose: '',
        message: ''
    });

    // State to manage form submission status
    const [formStatus, setFormStatus] = useState({
        submitted: false,
        success: false,
        errorMessage: ''
    });

    // Handle input changes and update state
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevent page reload on form submit

        // Add access key from Web3Forms here
        const accessKey = 'd61cda56-8796-427f-9489-6e8bda9206c4'; // Web3Forms access key

        const formURL = 'https://api.web3forms.com/submit'; // API endpoint for form submission
        const formPayload = {
            access_key: accessKey,
            ...formData
        };

        try {
            const response = await fetch(formURL, {
                method: 'POST', // HTTP method
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formPayload), // Send form data as JSON
            });

            const result = await response.json();

            if (response.ok) {
                // Successful form submission
                setFormStatus({
                    submitted: true,
                    success: true,
                    errorMessage: ''
                });
                setFormData({ fullName: '', email: '', purpose: '', message: '' }); // Reset form fields
            } else {
                // Form submission error
                setFormStatus({
                    submitted: true,
                    success: false,
                    errorMessage: result.message || 'Something went wrong. Please try again.'
                });
            }
        } catch (error) {
            // Handle network errors
            setFormStatus({
                submitted: true,
                success: false,
                errorMessage: 'An error occurred. Please try again later.'
            });
        }
    };

    return (
        // Added contactUs-page wrapper for custom styles
        <div className="contact-page">
            <div className="contact-container">
                <p className="title-text">CONTACT</p>
                <form className="contact-form" onSubmit={handleSubmit}>
                    {/* Full Name input */}
                    <div className="form-group">
                        <label htmlFor="fullName">Full Name</label>
                        <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            placeholder="Name"
                            value={formData.fullName}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    {/* Email input */}
                    <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            placeholder="Email@example.com"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    {/* Purpose dropdown */}
                    <div className="form-group">
                        <label htmlFor="purpose">Purpose</label>
                        <select
                            id="purpose"
                            name="purpose"
                            value={formData.purpose}
                            onChange={handleChange}
                            required
                        >
                            <option value="" disabled selected>Purpose</option>
                            {/* Grey placeholder */}
                            <option value="generalInquiry">General Inquiry</option>
                            <option value="cooperation">Cooperation</option>
                            <option value="joinUs">Join Us</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    {/* Message text area */}
                    <div className="form-group">
                        <label htmlFor="message">Message</label>
                        <textarea
                            id="message"
                            name="message"
                            placeholder="Message"
                            value={formData.message}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    {/* Submit button */}
                    <button type="submit" className="submit-button">Send</button>

                    {/* Display form status */}
                    {formStatus.submitted && (
                        <div className={`form-status ${formStatus.success ? 'success' : 'error'}`}>
                            <div className="modal">
                                <div className="modal-content">
                                    <p>
                                        {formStatus.success
                                            ? "Your message has been sent successfully!"
                                            : formStatus.errorMessage}
                                    </p>
                                    <button className="close-button" onClick={() => setFormStatus({
                                        ...formStatus,
                                        submitted: false
                                    })}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default Contact;
